import type {
  AttemptResult, FurthestStep, PortalSourcesPage, ShieldAnswer, SortDirection, SourcesFilters, SourcesSort, WhoBucket,
} from "@/types";

export const SOURCES_PAGE_SIZE = 50;
export const RECENT_DAYS = 7;
const MAX_DAYS = 365;
// The BFF answers 422 to a longer search.
export const MAX_SEARCH_LENGTH = 64;
export const LISTED_GROUPS: readonly string[] = ["secretFiles", "knownVulnerabilities", "exploits", "scannerTools", "softwareProbes"];
export const FURTHEST_STEP_ORDER: readonly FurthestStep[] = ["neverReachedSignIn", "reachedNoAttempt", "refused", "failed", "signedIn"];
export const ANSWER_ORDER: readonly ShieldAnswer[] = ["blocked", "captcha", "allowed", "monitored", "notAsked", "other"];
export const ATTEMPT_ORDER: readonly AttemptResult[] = ["blocked", "userNotFound", "invalidPassword", "failedSecondFactor", "signedIn"];
export const WHO_ORDER: readonly WhoBucket[] = ["researchScanner", "crawler", "otherKnown", "notClassified"];
export const SOURCES_SORTS: readonly SourcesSort[] = ["requests", "attempts", "lastSeen", "firstSeen"];

export const EMPTY_SOURCES_FILTERS: SourcesFilters = {
  search: "",
  groups: [],
  rules: [],
  yourProduct: false,
  furthestSteps: [],
  answers: [],
  attempts: [],
  who: [],
  countries: [],
  networks: [],
  firstSeenDays: null,
  named: false,
  namedLater: false,
  sort: "requests",
  order: "desc",
};

type ListKey = "groups" | "rules" | "furthestSteps" | "answers" | "attempts" | "who" | "countries" | "networks";
type FlagKey = "yourProduct" | "named" | "namedLater";

const LIST_PARAMS: readonly (readonly [ListKey, string])[] = [
  ["groups", "group"], ["rules", "rule"], ["furthestSteps", "furthest_step"], ["answers", "answer"],
  ["attempts", "attempt"], ["who", "who"], ["countries", "country"], ["networks", "network"],
];
const FLAG_PARAMS: readonly (readonly [FlagKey, string])[] = [
  ["yourProduct", "your_product"], ["named", "named"], ["namedLater", "named_later"],
];

export interface SourcesRange {
  readonly from: number;
  readonly to: number;
  readonly total: number | null;
  readonly pageCount: number;
}

function onlyKnown<T extends string>(values: readonly string[], known: readonly T[]): T[] {
  return values.filter((value): value is T => (known as readonly string[]).includes(value));
}

function readSort(value: string | null): SourcesSort {
  return SOURCES_SORTS.find((sort) => sort === value) ?? "requests";
}

function readDays(value: string | null): number | null {
  const days = Number(value);
  return Number.isInteger(days) && days > 0 && days <= MAX_DAYS ? days : null;
}

/** The Sources filters a URL holds; a value Insight does not know is dropped. */
export function parseSourcesFilters(params: URLSearchParams): SourcesFilters {
  const order: SortDirection = params.get("order") === "asc" ? "asc" : "desc";
  return {
    search: params.get("search") ?? "",
    groups: params.getAll("group"),
    rules: params.getAll("rule"),
    yourProduct: params.get("your_product") === "true",
    furthestSteps: onlyKnown(params.getAll("furthest_step"), FURTHEST_STEP_ORDER),
    answers: onlyKnown(params.getAll("answer"), ANSWER_ORDER),
    attempts: onlyKnown(params.getAll("attempt"), ATTEMPT_ORDER),
    who: onlyKnown(params.getAll("who"), WHO_ORDER),
    countries: params.getAll("country"),
    networks: params.getAll("network"),
    firstSeenDays: readDays(params.get("first_seen_days")),
    named: params.get("named") === "true",
    namedLater: params.get("named_later") === "true",
    sort: readSort(params.get("sort")),
    order,
  };
}

export function parseSourcesPage(params: URLSearchParams): number {
  const page = Number(params.get("page"));
  return Number.isInteger(page) && page > 0 ? page : 0;
}

/** The filters as query parameters, defaults left out. The URL and the BFF read the same names. */
export function buildSourcesFilterParams(filters: SourcesFilters): URLSearchParams {
  const params = new URLSearchParams();
  const search = filters.search.trim().slice(0, MAX_SEARCH_LENGTH);
  if (search) params.set("search", search);
  for (const [key, name] of LIST_PARAMS) {
    for (const value of filters[key]) params.append(name, value);
  }
  for (const [key, name] of FLAG_PARAMS) {
    if (filters[key]) params.set(name, "true");
  }
  if (filters.firstSeenDays !== null) params.set("first_seen_days", String(filters.firstSeenDays));
  if (filters.sort !== "requests") params.set("sort", filters.sort);
  if (filters.order !== "desc") params.set("order", filters.order);
  return params;
}

export function buildSourcesUrlParams(filters: SourcesFilters, page: number): URLSearchParams {
  const params = new URLSearchParams({ tab: "sources" });
  for (const [name, value] of buildSourcesFilterParams(filters)) params.append(name, value);
  if (page > 0) params.set("page", String(page));
  return params;
}

export function sourcesHref(filters: Partial<SourcesFilters>): string {
  return `/vulnerability-watch?${buildSourcesUrlParams({ ...EMPTY_SOURCES_FILTERS, ...filters }, 0).toString()}`;
}

/** First seen within 7 days, and within the window once the snapshot says how long it is. */
export function firstSeenOptions(windowDays: number | null): number[] {
  return windowDays !== null && windowDays > RECENT_DAYS ? [RECENT_DAYS, windowDays] : [RECENT_DAYS];
}

export function buildSourcesRange(page: number, data: PortalSourcesPage | undefined): SourcesRange {
  const total = data?.available ? data.total : null;
  if (total === null) return { from: 0, to: 0, total: null, pageCount: 1 };
  return {
    from: total === 0 ? 0 : page * SOURCES_PAGE_SIZE + 1,
    to: Math.min((page + 1) * SOURCES_PAGE_SIZE, total),
    total,
    pageCount: Math.max(1, Math.ceil(total / SOURCES_PAGE_SIZE)),
  };
}
